import React, { useState, useEffect, useCallback, useImperativeHandle, forwardRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { config } from '../../config/environment';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { 
  DollarSign,
  Calculator,
  Package,
  Layers,
  Lock,
  Unlock,
  RefreshCw,
  TrendingUp,
  AlertCircle,
  ExternalLink,
  Zap,
  Settings,
  ToggleLeft,
  ToggleRight
} from 'lucide-react';

const API = config.apiBaseUrl;
const MOCK_TENANT_ID = 'tenant-123';

// ============================================================
// EDITABLE COST ROW COMPONENT - With percentage display and edit
// ============================================================
const EditableCostRow = ({ 
  label, 
  field, 
  currencySymbol, 
  standardCosts, 
  standardPercents,
  handleStandardCostChange, 
  handleStandardCostBlur, 
  setIsEditingStandardCost,
  baseValue,  // The base value to calculate % from (e.g., raw materials for labor)
  baseLabel,  // Label for base (e.g., "of raw materials")
  onPercentChange,
  onPercentBlur,
  editingPercent,
  setEditingPercent
}) => {
  const dollarValue = parseFloat(standardCosts[field]) || 0;
  const percentField = `${field}_percent`;
  const storedPercent = standardPercents?.[percentField];
  
  // Calculate percent from dollar value and base
  const calculatedPercent = baseValue > 0 ? ((dollarValue / baseValue) * 100) : 0;
  
  // Show stored percent if available, otherwise calculated
  const displayPercent = storedPercent !== null && storedPercent !== undefined ? storedPercent : calculatedPercent;
  
  const [localPercent, setLocalPercent] = React.useState('');
  const isEditing = editingPercent?.[field];
  
  const handlePercentClick = () => {
    if (baseValue > 0 && setEditingPercent) {
      setLocalPercent(displayPercent.toFixed(1));
      setEditingPercent(prev => ({ ...prev, [field]: true }));
    }
  };
  
  const handlePercentSave = () => {
    const newPercent = parseFloat(localPercent);
    if (!isNaN(newPercent) && baseValue > 0) {
      // Calculate new dollar value from percent
      const newDollarValue = (baseValue * newPercent / 100).toFixed(2);
      onPercentChange?.(field, newPercent, newDollarValue);
    }
    setEditingPercent?.(prev => ({ ...prev, [field]: false }));
  };
  
  const handlePercentCancel = () => {
    setEditingPercent?.(prev => ({ ...prev, [field]: false }));
  };
  
  const handlePercentKeyDown = (e) => {
    if (e.key === 'Enter') handlePercentSave();
    if (e.key === 'Escape') handlePercentCancel();
  };
  
  return (
    <div className="flex justify-between items-center text-xs">
      <span className="text-gray-400">{label}</span>
      <div className="flex items-center gap-2">
        {/* Dollar input */}
        <div className="flex items-center gap-1">
          <span className="text-gray-500">{currencySymbol}</span>
          <input
            type="text"
            inputMode="decimal"
            value={standardCosts[field]}
            onChange={(e) => handleStandardCostChange(field, e.target.value)}
            onFocus={() => setIsEditingStandardCost(prev => ({ ...prev, [field]: true }))}
            className="w-20 h-6 px-2 text-xs text-right bg-gray-700 border border-gray-600 hover:border-blue-500 focus:border-blue-500 text-white rounded font-mono"
            data-testid={`standard-cost-${field}`}
          />
        </div>
        
        {/* Percentage display/edit */}
        {baseValue > 0 && (
          <>
            {isEditing ? (
              <div className="flex items-center gap-1">
                <input
                  type="text"
                  inputMode="decimal"
                  value={localPercent}
                  onChange={(e) => setLocalPercent(e.target.value)}
                  onKeyDown={handlePercentKeyDown}
                  autoFocus
                  className="w-12 h-5 px-1 text-xs text-center bg-gray-700 border border-purple-500 text-white rounded"
                  data-testid={`standard-percent-${field}-input`}
                />
                <span className="text-gray-400 text-[10px]">%</span>
                <button
                  onClick={handlePercentSave}
                  className="px-1 py-0.5 text-[10px] bg-green-600 text-white rounded hover:bg-green-500"
                >
                  ✓
                </button>
                <button
                  onClick={handlePercentCancel}
                  className="px-1 py-0.5 text-[10px] bg-gray-600 text-white rounded hover:bg-gray-500"
                >
                  ✗
                </button>
              </div>
            ) : (
              <button
                onClick={handlePercentClick}
                className="flex items-center gap-1 px-1.5 py-0.5 text-[10px] text-gray-400 hover:text-purple-400 hover:bg-gray-700 rounded transition-colors"
                title={baseLabel ? `${displayPercent.toFixed(1)}% ${baseLabel}` : `${displayPercent.toFixed(1)}%`}
                data-testid={`standard-percent-${field}`}
              >
                <span>({displayPercent.toFixed(1)}%)</span>
                <svg className="w-2.5 h-2.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                </svg>
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

// Safe number formatter - prevents .toFixed() crashes on null/undefined/non-numbers
const safeNumber = (value) => {
  if (value === null || value === undefined || value === '') return 0;
  const num = parseFloat(value);
  return isNaN(num) ? 0 : num;
};

const safeToFixed = (value, decimals = 2) => {
  const num = parseFloat(value);
  if (isNaN(num) || value === null || value === undefined) {
    return decimals === 1 ? "0.0" : "0.00";
  }
  return num.toFixed(decimals);
};

const PricingTab = forwardRef(({ 
  productId, 
  productData, 
  onFieldChange,
  onNavigateToBOM = () => {},
  onDirtyChange = () => {},  // Callback when dirty state changes
  onSaveComplete = () => {}   // Callback after successful save
}, ref) => {
  // ============================================================
  // ALL HOOKS MUST BE AT TOP LEVEL - NO CONDITIONAL HOOKS
  // ============================================================
  
  // Track unsaved changes (isDirty state)
  const [isDirty, setIsDirty] = useState(false);
  
  // Notify parent when dirty state changes
  useEffect(() => {
    console.log('💾 PricingTab isDirty changed:', isDirty);
    onDirtyChange(isDirty);
  }, [isDirty, onDirtyChange]);
  
  // Browser beforeunload handler to prevent accidental navigation
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?';
        return e.returnValue;
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);
  
  const [controlSheet, setControlSheet] = useState({
    labor_percent: 10,
    import_shipping_percent: 50,
    packaging_handling_percent: 25,
    shipping_percent: 0,
    marketing_percent: 15,
    wholesale_margin_target: 40,
    retail_margin_target: 60
  });
  const [loading, setLoading] = useState(false);
  const [costingData, setCostingData] = useState(null);
  
  // Cost override states - null means use global, number means override
  const [costOverrides, setCostOverrides] = useState({
    labor_percent: null,
    import_shipping_percent: null,
    packaging_handling_percent: null,
    shipping_percent: null,
    marketing_percent: null
  });
  
  // Pricing config per channel (shipping_paid_by)
  const [pricingConfig, setPricingConfig] = useState({
    wholesale: { shipping_paid_by: 'seller' },
    retail: { shipping_paid_by: 'seller' }
  });
  
  // Track which overrides are being edited
  const [editingOverride, setEditingOverride] = useState({
    labor: false,
    import_shipping: false,
    packaging_handling: false,
    shipping: false,
    marketing: false
  });
  
  // Local input states for smooth typing (separate from costOverrides)
  const [inputValues, setInputValues] = useState({
    labor_percent: '',
    import_shipping_percent: '',
    packaging_handling_percent: '',
    shipping_percent: '',
    marketing_percent: ''
  });
  
  // State for editable standard product costs - ALWAYS DECLARED
  const [standardCosts, setStandardCosts] = useState({
    raw_materials_cost: '',
    labor_cost: '',
    factory_overhead: '',
    freight_cost: '',
    duties_cost: '',
    handling_cost: '',
    fulfillment_cost: ''
  });
  const [isEditingStandardCost, setIsEditingStandardCost] = useState({});
  
  // State for cost percentages
  const [standardPercents, setStandardPercents] = useState({
    labor_cost_percent: null,
    factory_overhead_percent: null,
    freight_cost_percent: null,
    duties_cost_percent: null,
    handling_cost_percent: null
  });
  
  // State for tracking which percent is being edited
  const [editingPercent, setEditingPercent] = useState({});
  
  // Track if user is actively typing in an input
  const [isTyping, setIsTyping] = useState({
    labor_percent: false,
    import_shipping_percent: false,
    packaging_handling_percent: false,
    shipping_percent: false,
    marketing_percent: false
  });

  // Determine product type
  const productType = productData.product_type || 'finished_product';
  const isRawMaterial = productType === 'raw_material';
  const isFinishedProduct = productType === 'finished_product';
  const isStandardProduct = productType === 'standard';

  // Debounce timer ref for API calls
  const saveTimeoutRef = React.useRef(null);
  
  // Ref to track if we're currently in the middle of a blur operation
  // This prevents the useEffect from overwriting values during save
  const isBlurringRef = React.useRef(false);
  
  // Ref to track the last changed field to prevent sync conflicts
  const lastChangedFieldRef = React.useRef(null);

  // Get currency symbol
  const getCurrencySymbol = (currency) => {
    const symbols = { 'USD': '$', 'INR': '₹', 'EUR': '€', 'GBP': '£', 'CAD': 'C$' };
    return symbols[currency] || '$';
  };

  const currency = productData.currency || 'USD';
  const currencySymbol = getCurrencySymbol(currency);
  
  // Helper to mark form as dirty (has unsaved changes)
  const markDirty = useCallback(() => {
    if (!isDirty) {
      console.log('🔴 Marking PricingTab as DIRTY');
      setIsDirty(true);
    }
  }, [isDirty]);
  
  // Helper to clear dirty state after successful save
  const clearDirty = useCallback(() => {
    console.log('✅ Clearing PricingTab dirty state');
    setIsDirty(false);
    onSaveComplete();
  }, [onSaveComplete]);
  
  // ============================================================
  // EXPOSE SAVE FUNCTION VIA REF FOR PARENT COMPONENT
  // ============================================================
  
  // Function to trigger save of any pending changes
  const triggerSave = useCallback(async () => {
    console.log('🔵 PricingTab triggerSave called');
    
    if (!isDirty) {
      console.log('✅ No unsaved changes to save');
      return;
    }
    
    // For standard products, save the current standardCosts
    if (productData.product_type === 'standard' && productId) {
      try {
        const updates = {
          raw_materials_cost: parseFloat(standardCosts.raw_materials_cost) || 0,
          labor_cost: parseFloat(standardCosts.labor_cost) || 0,
          factory_overhead: parseFloat(standardCosts.factory_overhead) || 0,
          freight_cost: parseFloat(standardCosts.freight_cost) || 0,
          duties_cost: parseFloat(standardCosts.duties_cost) || 0,
          handling_cost: parseFloat(standardCosts.handling_cost) || 0,
          fulfillment_cost: parseFloat(standardCosts.fulfillment_cost) || 0
        };
        
        await axios.patch(`${API}/product-master/item/${productId}`, updates, {
          params: { tenant_id: MOCK_TENANT_ID },
          headers: { 'X-Tenant-ID': MOCK_TENANT_ID }
        });
        
        clearDirty();
        toast.success('Changes saved successfully');
      } catch (error) {
        console.error('Error saving changes:', error);
        throw error;
      }
    } else if (productId) {
      // For other product types with cost overrides
      try {
        await axios.patch(`${API}/products/${productId}/costing`, 
          { cost_overrides: costOverrides },
          { params: { tenant_id: MOCK_TENANT_ID }, headers: { 'X-Tenant-ID': MOCK_TENANT_ID } }
        );
        
        clearDirty();
        toast.success('Changes saved successfully');
      } catch (error) {
        console.error('Error saving changes:', error);
        throw error;
      }
    }
  }, [isDirty, productId, productData.product_type, standardCosts, costOverrides, clearDirty]);
  
  // Expose triggerSave to parent via ref
  useImperativeHandle(ref, () => ({
    triggerSave
  }), [triggerSave]);

  // ============================================================
  // ALL useCallback HOOKS - DECLARED AT TOP LEVEL
  // ============================================================

  // Fetch control sheet settings
  const fetchControlSheet = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/settings/costing-control-sheet`, {
        params: { tenant_id: MOCK_TENANT_ID }
      });
      if (response.data.success && response.data.data) {
        setControlSheet(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching control sheet:', error);
    }
  }, []);

  // Fetch product costing data
  const fetchCostingData = useCallback(async () => {
    if (!productId) return;
    
    try {
      setLoading(true);
      const response = await axios.get(`${API}/products/${productId}/bom`, {
        params: { tenant_id: MOCK_TENANT_ID },
        headers: { 'X-Tenant-ID': MOCK_TENANT_ID }
      });
      
      if (response.data.success) {
        setCostingData(response.data);
      }
    } catch (error) {
      console.error('Error fetching costing data:', error);
    } finally {
      setLoading(false);
    }
  }, [productId]);

  // Handle standard cost change - DECLARED AT TOP LEVEL
  const handleStandardCostChange = useCallback((field, value) => {
    if (value !== '' && !/^\d*\.?\d*$/.test(value)) return;
    setStandardCosts(prev => ({ ...prev, [field]: value }));
    markDirty(); // Mark as dirty when user edits
  }, [markDirty]);

  // Handle standard cost blur - save to backend - DECLARED AT TOP LEVEL
  const handleStandardCostBlur = useCallback(async (field) => {
    setIsEditingStandardCost(prev => ({ ...prev, [field]: false }));
    
    const numValue = parseFloat(standardCosts[field]) || 0;
    
    if (productId) {
      try {
        await axios.patch(`${API}/product-master/item/${productId}`, 
          { [field]: numValue },
          { params: { tenant_id: MOCK_TENANT_ID }, headers: { 'X-Tenant-ID': MOCK_TENANT_ID } }
        );
        onFieldChange(field, numValue);
        clearDirty(); // Clear dirty state after successful save
        toast.success(`${field.replace(/_/g, ' ')} updated`);
      } catch (error) {
        console.error('Error saving standard cost:', error);
        toast.error(`Failed to save cost: ${error.response?.data?.detail || error.message}`);
      }
    }
  }, [productId, standardCosts, onFieldChange, clearDirty]);

  // Handle percent change - recalculates dollar value and saves both
  const handlePercentChange = useCallback(async (field, newPercent, newDollarValue) => {
    const percentField = `${field}_percent`;
    
    // Update local state
    setStandardCosts(prev => ({ ...prev, [field]: newDollarValue }));
    setStandardPercents(prev => ({ ...prev, [percentField]: newPercent }));
    markDirty(); // Mark as dirty when user edits
    
    if (productId) {
      try {
        // Save both dollar and percent values
        await axios.patch(`${API}/product-master/item/${productId}`, 
          { 
            [field]: parseFloat(newDollarValue),
            [percentField]: newPercent
          },
          { params: { tenant_id: MOCK_TENANT_ID }, headers: { 'X-Tenant-ID': MOCK_TENANT_ID } }
        );
        onFieldChange(field, parseFloat(newDollarValue));
        onFieldChange(percentField, newPercent);
        clearDirty(); // Clear dirty state after successful save
        toast.success(`${field.replace(/_/g, ' ')} updated to ${newPercent}%`);
      } catch (error) {
        console.error('Error saving percent:', error);
        toast.error(`Failed to save: ${error.response?.data?.detail || error.message}`);
      }
    }
  }, [productId, onFieldChange, markDirty, clearDirty]);

  // ============================================================
  // ALL useEffect HOOKS - DECLARED AT TOP LEVEL
  // ============================================================

  useEffect(() => {
    fetchControlSheet();
    fetchCostingData();
  }, [fetchControlSheet, fetchCostingData]);
  
  // Load cost overrides from product data
  useEffect(() => {
    if (productData.cost_overrides) {
      setCostOverrides({
        labor_percent: productData.cost_overrides.labor_percent ?? null,
        import_shipping_percent: productData.cost_overrides.import_shipping_percent ?? null,
        packaging_handling_percent: productData.cost_overrides.packaging_handling_percent ?? null,
        shipping_percent: productData.cost_overrides.shipping_percent ?? null,
        marketing_percent: productData.cost_overrides.marketing_percent ?? null
      });
    }
    
    // Load pricing config (shipping_paid_by per channel)
    if (productData.pricing) {
      setPricingConfig({
        wholesale: {
          shipping_paid_by: productData.pricing?.wholesale?.shipping_paid_by || 'seller'
        },
        retail: {
          shipping_paid_by: productData.pricing?.retail?.shipping_paid_by || 'seller'
        }
      });
    }
  }, [productData.cost_overrides, productData.pricing]);
  
  // Track if any input is being typed (to prevent external resets)
  const isAnyInputTyping = Object.values(isTyping).some(v => v);
  
  // Sync inputValues when costOverrides change from external source (e.g., API load)
  // ONLY sync when user is NOT typing and NOT in blur operation
  useEffect(() => {
    // Don't sync while user is typing or we're processing a blur
    if (isAnyInputTyping || isBlurringRef.current) {
      return;
    }
    
    // Use a small delay to prevent race conditions with blur operations
    const syncTimer = setTimeout(() => {
      // Double-check we're still not in a blur operation
      if (isBlurringRef.current) return;
      
      setInputValues({
        labor_percent: costOverrides.labor_percent !== null ? String(costOverrides.labor_percent) : '',
        import_shipping_percent: costOverrides.import_shipping_percent !== null ? String(costOverrides.import_shipping_percent) : '',
        packaging_handling_percent: costOverrides.packaging_handling_percent !== null ? String(costOverrides.packaging_handling_percent) : '',
        shipping_percent: costOverrides.shipping_percent !== null ? String(costOverrides.shipping_percent) : '',
        marketing_percent: costOverrides.marketing_percent !== null ? String(costOverrides.marketing_percent) : ''
      });
    }, 100); // Small delay to let blur complete
    
    return () => clearTimeout(syncTimer);
  }, [productData.cost_overrides, isAnyInputTyping, costOverrides]);

  // Initialize standard costs from product data - ALWAYS RUNS BUT ONLY UPDATES FOR STANDARD PRODUCTS
  useEffect(() => {
    if (isStandardProduct && productData) {
      const pricing = productData.pricing_v2 || {};
      setStandardCosts({
        raw_materials_cost: String(productData.raw_materials_cost ?? productData.raw_material_cost ?? pricing.raw_materials_cost ?? 0),
        labor_cost: String(productData.labor_cost ?? pricing.labor_cost ?? 0),
        factory_overhead: String(productData.factory_overhead ?? pricing.factory_overhead ?? 0),
        freight_cost: String(productData.freight_cost ?? pricing.freight_cost ?? 0),
        duties_cost: String(productData.duties_cost ?? pricing.duties_cost ?? 0),
        handling_cost: String(productData.handling_cost ?? pricing.handling_cost ?? 0),
        fulfillment_cost: String(productData.fulfillment_cost ?? pricing.fulfillment_cost ?? 0)
      });
      
      // Load stored percentages
      setStandardPercents({
        labor_cost_percent: pricing.labor_cost_percent ?? productData.labor_cost_percent ?? null,
        factory_overhead_percent: pricing.factory_overhead_percent ?? productData.factory_overhead_percent ?? null,
        freight_cost_percent: pricing.freight_cost_percent ?? productData.freight_cost_percent ?? null,
        duties_cost_percent: pricing.duties_cost_percent ?? productData.duties_cost_percent ?? null,
        handling_cost_percent: pricing.handling_cost_percent ?? productData.handling_cost_percent ?? null
      });
    }
  }, [isStandardProduct, productData]);

  // ============================================================
  // HELPER FUNCTIONS (not hooks, safe to use anywhere)
  // ============================================================
  
  // Get effective percentage (override or global)
  const getEffectivePercent = (type) => {
    const overrideValue = costOverrides[`${type}_percent`];
    if (overrideValue !== null && overrideValue !== undefined) {
      return overrideValue;
    }
    return controlSheet[`${type}_percent`] || 0;
  };
  
  // Check if using override
  const isUsingOverride = (type) => {
    const overrideValue = costOverrides[`${type}_percent`];
    return overrideValue !== null && overrideValue !== undefined;
  };
  
  // Handle shipping_paid_by change for a channel
  const handleShippingPaidByChange = async (channel, value) => {
    try {
      await axios.patch(`${API}/products/${productId}/pricing-config`, null, {
        params: {
          tenant_id: MOCK_TENANT_ID,
          channel,
          shipping_paid_by: value
        }
      });
      
      setPricingConfig(prev => ({
        ...prev,
        [channel]: { ...prev[channel], shipping_paid_by: value }
      }));
      
      // Trigger recalculation to update suggested prices
      await fetchCostingData();
      toast.success(`${channel} shipping updated to ${value}`);
    } catch (error) {
      console.error('Error updating shipping config:', error);
      toast.error('Failed to update shipping config');
    }
  };
  
  // Handle override toggle
  const handleOverrideToggle = async (type) => {
    const currentOverride = costOverrides[`${type}_percent`];
    const globalValue = controlSheet[`${type}_percent`];
    
    let newOverrideValue;
    if (currentOverride !== null && currentOverride !== undefined) {
      // Currently using override, switch to global
      newOverrideValue = null;
    } else {
      // Currently using global, switch to override with global value as starting point
      newOverrideValue = globalValue;
    }
    
    const newOverrides = {
      ...costOverrides,
      [`${type}_percent`]: newOverrideValue
    };
    
    setCostOverrides(newOverrides);
    
    // Save to product and recalculate
    if (productId) {
      try {
        await axios.patch(`${API}/products/${productId}/costing`, 
          { cost_overrides: newOverrides },
          { params: { tenant_id: MOCK_TENANT_ID }, headers: { 'X-Tenant-ID': MOCK_TENANT_ID } }
        );
        onFieldChange('cost_overrides', newOverrides);
        
        // Trigger recalculation to update costs with new override
        await axios.post(`${API}/products/${productId}/recalculate-costs`, null, {
          params: { tenant_id: MOCK_TENANT_ID, force: true },
          headers: { 'X-Tenant-ID': MOCK_TENANT_ID }
        });
        
        // Refresh costing data to show updated values
        fetchCostingData();
        clearDirty(); // Clear dirty state after successful save
        
        toast.success(newOverrideValue !== null ? 'Using custom percentage' : 'Using global percentage');
      } catch (error) {
        console.error('Error saving override:', error);
        toast.error('Failed to save override');
      }
    } else {
      onFieldChange('cost_overrides', newOverrides);
    }
  };
  
  // Handle input focus - mark as typing immediately
  const handleInputFocus = (type) => {
    const key = `${type}_percent`;
    setIsTyping(prev => ({
      ...prev,
      [key]: true
    }));
  };
  
  // Handle input change - ONLY updates local string state, no calculations
  const handleInputChange = (type, value) => {
    // Allow empty string, numbers, and decimal points only
    if (value !== '' && !/^\d*\.?\d*$/.test(value)) {
      return; // Reject invalid input
    }
    
    // Update local input state immediately (no parsing, no calculations)
    setInputValues(prev => ({
      ...prev,
      [`${type}_percent`]: value
    }));
    
    // Mark as actively typing (in case focus didn't fire first)
    setIsTyping(prev => ({
      ...prev,
      [`${type}_percent`]: true
    }));
    
    // Mark form as dirty (unsaved changes)
    markDirty();
  };
  
  // Handle blur - commits value and triggers calculation
  const handleInputBlur = async (type) => {
    const key = `${type}_percent`;
    const rawValue = inputValues[key];
    
    // Set blur flag to prevent useEffect from resetting values
    isBlurringRef.current = true;
    lastChangedFieldRef.current = key;
    
    // Mark as no longer typing
    setIsTyping(prev => ({
      ...prev,
      [key]: false
    }));
    
    // Parse and clamp the value
    const numValue = parseFloat(rawValue) || 0;
    const clampedValue = Math.min(999, Math.max(0, numValue));
    
    // Update costOverrides with the final value
    const newOverrides = {
      ...costOverrides,
      [key]: clampedValue
    };
    setCostOverrides(newOverrides);
    
    // Update input to show the clamped/cleaned value
    setInputValues(prev => ({
      ...prev,
      [key]: String(clampedValue)
    }));
    
    // Save to backend
    if (productId) {
      try {
        await axios.patch(`${API}/products/${productId}/costing`, 
          { cost_overrides: newOverrides },
          { params: { tenant_id: MOCK_TENANT_ID }, headers: { 'X-Tenant-ID': MOCK_TENANT_ID } }
        );
        onFieldChange('cost_overrides', newOverrides);
        
        // Trigger backend recalculation
        await axios.post(`${API}/products/${productId}/recalculate-costs`, null, {
          params: { tenant_id: MOCK_TENANT_ID, force: true },
          headers: { 'X-Tenant-ID': MOCK_TENANT_ID }
        });
        
        // Refresh costing data
        fetchCostingData();
        clearDirty(); // Clear dirty state after successful save
      } catch (error) {
        console.error('Error saving override value:', error);
        toast.error('Failed to save value');
      } finally {
        // Clear blur flag after a short delay to allow state updates to complete
        setTimeout(() => {
          isBlurringRef.current = false;
          lastChangedFieldRef.current = null;
        }, 200);
      }
    } else {
      onFieldChange('cost_overrides', newOverrides);
      // Clear blur flag
      setTimeout(() => {
        isBlurringRef.current = false;
        lastChangedFieldRef.current = null;
      }, 200);
    }
  };
  
  // Handle Enter key - same as blur
  const handleInputKeyDown = (e, type) => {
    if (e.key === 'Enter') {
      e.target.blur(); // This will trigger handleInputBlur
    }
  };
  
  // Get display value for input - use local input state if typing, otherwise use costOverrides
  const getInputDisplayValue = (type) => {
    const key = `${type}_percent`;
    // Always show local input value while typing or during blur
    if (isTyping[key] || isBlurringRef.current) {
      return inputValues[key];
    }
    return costOverrides[key] !== null ? String(costOverrides[key]) : '';
  };
  
  // DEPRECATED: Keep for backward compatibility but use new handlers
  const handleOverrideValueChange = (type, value) => {
    handleInputChange(type, value);
  };

  // Calculate raw material costs - use effective percentages (override or global)
  const calculateRawMaterialCosts = () => {
    const rawCost = parseFloat(productData.raw_material_cost) || 0;
    const laborPercent = getEffectivePercent('labor');
    const importPercent = getEffectivePercent('import_shipping');
    
    const laborCost = rawCost * (laborPercent / 100);
    const importCost = rawCost * (importPercent / 100);
    const landedCost = rawCost + laborCost + importCost;
    
    return { rawCost, laborCost, importCost, landedCost, laborPercent, importPercent };
  };

  // Update raw material cost and trigger recalculation
  const handleRawMaterialCostChange = async (value) => {
    onFieldChange('raw_material_cost', value);
    
    // Calculate new costs using effective percentages
    const rawCost = parseFloat(value) || 0;
    const laborPercent = getEffectivePercent('labor');
    const importPercent = getEffectivePercent('import_shipping');
    
    const laborCost = rawCost * (laborPercent / 100);
    const importCost = rawCost * (importPercent / 100);
    const landedCost = rawCost + laborCost + importCost;
    
    // Update calculated fields
    onFieldChange('labor_cost', laborCost);
    onFieldChange('import_shipping_cost', importCost);
    onFieldChange('landed_cost', landedCost);
  };

  // Toggle costing mode
  const handleToggleCostingMode = async () => {
    const newMode = productData.costing_mode === 'auto' ? 'manual' : 'auto';
    onFieldChange('costing_mode', newMode);
    
    if (productId) {
      try {
        await axios.patch(`${API}/products/${productId}/costing`, 
          { costing_mode: newMode },
          { params: { tenant_id: MOCK_TENANT_ID }, headers: { 'X-Tenant-ID': MOCK_TENANT_ID } }
        );
        toast.success(`Costing mode set to ${newMode.toUpperCase()}`);
      } catch (error) {
        console.error('Error updating costing mode:', error);
        toast.error('Failed to update costing mode');
      }
    }
  };

  // Toggle price mode (wholesale/retail)
  const handleTogglePriceMode = async (priceType) => {
    const modeField = `${priceType}_price_mode`;
    const newMode = productData[modeField] === 'auto' ? 'manual' : 'auto';
    onFieldChange(modeField, newMode);
    
    if (productId) {
      try {
        await axios.patch(`${API}/products/${productId}/costing`,
          { [modeField]: newMode },
          { params: { tenant_id: MOCK_TENANT_ID }, headers: { 'X-Tenant-ID': MOCK_TENANT_ID } }
        );
      } catch (error) {
        console.error('Error toggling price mode:', error);
      }
    }
  };

  // Recalculate costs (force=true to recalculate even in manual mode)
  const handleRecalculate = async () => {
    if (!productId) {
      toast.warning('Save the product first to recalculate costs');
      return;
    }
    
    try {
      setLoading(true);
      await axios.post(`${API}/products/${productId}/recalculate-costs`, null, {
        params: { tenant_id: MOCK_TENANT_ID, force: true },
        headers: { 'X-Tenant-ID': MOCK_TENANT_ID }
      });
      toast.success('Costs recalculated');
      fetchCostingData();
    } catch (error) {
      console.error('Error recalculating:', error);
      toast.error('Failed to recalculate costs');
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // RAW MATERIAL PRICING UI
  // ============================================================
  if (isRawMaterial) {
    const costs = calculateRawMaterialCosts();
    
    return (
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Package className="w-4 h-4 text-orange-400" />
            <span className="text-sm font-semibold text-white">Raw Material Costing</span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRecalculate}
              disabled={loading || !productId}
              className="h-7 text-xs"
              data-testid="recalculate-raw-costs-btn"
            >
              <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} />
              Recalculate
            </Button>
          </div>
        </div>

        {/* Costing Mode Toggle */}
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-yellow-400" />
                <span className="text-xs text-gray-300">Costing Mode</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleToggleCostingMode}
                  className={`px-3 py-1 text-xs rounded-full transition-colors ${
                    productData.costing_mode === 'auto'
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-700 text-gray-400'
                  }`}
                  data-testid="costing-mode-auto-btn"
                >
                  AUTO
                </button>
                <button
                  onClick={handleToggleCostingMode}
                  className={`px-3 py-1 text-xs rounded-full transition-colors ${
                    productData.costing_mode === 'manual'
                      ? 'bg-purple-600 text-white'
                      : 'bg-gray-700 text-gray-400'
                  }`}
                  data-testid="costing-mode-manual-btn"
                >
                  MANUAL
                </button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Base Cost Input */}
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-3">
            <div className="flex items-center gap-2 mb-3">
              <DollarSign className="w-4 h-4 text-green-400" />
              <span className="text-xs font-medium text-white uppercase tracking-wide">Base Cost</span>
            </div>
            <div>
              <label className="text-[10px] text-gray-400 uppercase tracking-wide mb-1 block">
                Raw Material Cost (per unit)
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">{currencySymbol}</span>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={productData.raw_material_cost || 0}
                  onChange={(e) => handleRawMaterialCostChange(parseFloat(e.target.value) || 0)}
                  className="pl-7 h-9 text-sm bg-gray-700 border-gray-600 text-white"
                  placeholder="0.00"
                  data-testid="raw-material-cost-input"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Cost Markups (from Control Sheet with Override Option) */}
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-3">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Calculator className="w-4 h-4 text-blue-400" />
                <span className="text-xs font-medium text-white uppercase tracking-wide">Cost Markups</span>
              </div>
              <Badge variant="outline" className="text-[10px] text-gray-400">
                <Settings className="w-3 h-3 mr-1" />
                Configurable
              </Badge>
            </div>
            
            <div className="space-y-3">
              {/* Labor Markup */}
              <div className="p-2 rounded-lg bg-gray-900/50">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-gray-300">+ Labor</span>
                  <span className="text-green-400 font-mono text-xs">+{currencySymbol}{safeToFixed(costs.laborCost)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleOverrideToggle('labor')}
                    className={`flex items-center gap-1 px-2 py-1 text-[10px] rounded transition-colors ${
                      isUsingOverride('labor')
                        ? 'bg-purple-600/30 text-purple-400 border border-purple-500'
                        : 'bg-gray-700 text-gray-400 border border-gray-600'
                    }`}
                    data-testid="labor-override-toggle"
                  >
                    {isUsingOverride('labor') ? (
                      <><Unlock className="w-3 h-3" /> Custom</>
                    ) : (
                      <><Lock className="w-3 h-3" /> Global</>
                    )}
                  </button>
                  {isUsingOverride('labor') ? (
                    <div className="flex items-center gap-1">
                      <input
                        type="text"
                        inputMode="decimal"
                        value={getInputDisplayValue('labor')}
                        onFocus={() => handleInputFocus('labor')}
                        onChange={(e) => handleInputChange('labor', e.target.value)}
                        onKeyDown={(e) => handleInputKeyDown(e, 'labor')}
                        className="w-16 h-6 px-2 text-xs text-center bg-gray-700 border border-purple-500 text-white rounded"
                        data-testid="labor-override-input"
                        placeholder="0"
                      />
                      <span className="text-[10px] text-gray-400">%</span>
                    </div>
                  ) : (
                    <span className="text-[10px] text-gray-400">{controlSheet.labor_percent}%</span>
                  )}
                </div>
              </div>

              {/* Import & Shipping Markup */}
              <div className="p-2 rounded-lg bg-gray-900/50">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs text-gray-300">+ Import & Shipping</span>
                  <span className="text-green-400 font-mono text-xs">+{currencySymbol}{safeToFixed(costs.importCost)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handleOverrideToggle('import_shipping')}
                    className={`flex items-center gap-1 px-2 py-1 text-[10px] rounded transition-colors ${
                      isUsingOverride('import_shipping')
                        ? 'bg-purple-600/30 text-purple-400 border border-purple-500'
                        : 'bg-gray-700 text-gray-400 border border-gray-600'
                    }`}
                    data-testid="import-override-toggle"
                  >
                    {isUsingOverride('import_shipping') ? (
                      <><Unlock className="w-3 h-3" /> Custom</>
                    ) : (
                      <><Lock className="w-3 h-3" /> Global</>
                    )}
                  </button>
                  {isUsingOverride('import_shipping') ? (
                    <div className="flex items-center gap-1">
                      <input
                        type="text"
                        inputMode="decimal"
                        value={getInputDisplayValue('import_shipping')}
                        onFocus={() => handleInputFocus('import_shipping')}
                        onChange={(e) => handleInputChange('import_shipping', e.target.value)}
                        onKeyDown={(e) => handleInputKeyDown(e, 'import_shipping')}
                        className="w-16 h-6 px-2 text-xs text-center bg-gray-700 border border-purple-500 text-white rounded"
                        data-testid="import-override-input"
                        placeholder="0"
                      />
                      <span className="text-[10px] text-gray-400">%</span>
                    </div>
                  ) : (
                    <span className="text-[10px] text-gray-400">{controlSheet.import_shipping_percent}%</span>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Landed Cost Result */}
        <Card className="bg-gradient-to-r from-blue-900/50 to-blue-800/50 border-blue-600">
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-blue-400" />
                <span className="text-xs font-medium text-white uppercase tracking-wide">Landed Cost</span>
              </div>
              <span className="text-xl font-bold text-white font-mono">
                {currencySymbol}{safeToFixed(costs.landedCost)}
              </span>
            </div>
            <p className="text-[10px] text-gray-400 mt-1">
              = {currencySymbol}{safeToFixed(costs.rawCost)} × (1 + {costs.laborPercent}% + {costs.importPercent}%)
              {(isUsingOverride('labor') || isUsingOverride('import_shipping')) && (
                <span className="text-purple-400 ml-1">(custom rates)</span>
              )}
            </p>
          </CardContent>
        </Card>

        {/* Info Note */}
        <div className="flex items-start gap-2 p-2 bg-gray-800/50 rounded-lg border border-gray-700">
          <AlertCircle className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
          <p className="text-[10px] text-gray-400">
            This landed cost will be used when this raw material is added as a component in finished products.
            {(isUsingOverride('labor') || isUsingOverride('import_shipping')) ? (
              <span className="text-purple-400"> Using custom markup rates for this product.</span>
            ) : (
              <span> To change global markup percentages, go to <span className="text-blue-400">Settings → Costing</span>.</span>
            )}
          </p>
        </div>
      </div>
    );
  }

  // ============================================================
  // STANDARD PRODUCT PRICING UI (Direct Costs - No BOM)
  // ============================================================
  if (isStandardProduct) {
    // Get direct cost values from product data or pricing_v2
    const pricing = productData.pricing_v2 || {};
    const rawMaterialsCost = parseFloat(standardCosts.raw_materials_cost) || 0;
    const laborCost = parseFloat(standardCosts.labor_cost) || 0;
    const factoryOverhead = parseFloat(standardCosts.factory_overhead) || 0;
    const freightCost = parseFloat(standardCosts.freight_cost) || 0;
    const dutiesCost = parseFloat(standardCosts.duties_cost) || 0;
    const handlingCost = parseFloat(standardCosts.handling_cost) || 0;
    const fulfillmentCostVal = parseFloat(standardCosts.fulfillment_cost) || 0;
    
    // Calculate totals
    const manufacturingCost = rawMaterialsCost + laborCost + factoryOverhead;
    const importShippingCost = freightCost + dutiesCost + handlingCost;
    const totalCogs = manufacturingCost + importShippingCost + fulfillmentCostVal;
    
    const wholesalePrice = safeNumber(productData.wholesale_price ?? pricing.wholesale_price);
    const retailPrice = safeNumber(productData.retail_price ?? pricing.retail_price);
    
    // Calculate margins
    const wholesaleMargin = wholesalePrice > 0 ? ((wholesalePrice - totalCogs) / wholesalePrice * 100) : 0;
    const retailMargin = retailPrice > 0 ? ((retailPrice - totalCogs) / retailPrice * 100) : 0;
    
    return (
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Package className="w-4 h-4 text-cyan-400" />
            <span className="text-sm font-semibold text-white">Standard Product Pricing</span>
            <Badge variant="outline" className="text-[10px] text-cyan-400 border-cyan-400">
              Direct Costs (Editable)
            </Badge>
          </div>
        </div>

        {/* Cost Breakdown */}
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-3">
            <div className="flex items-center gap-2 mb-3">
              <Calculator className="w-4 h-4 text-green-400" />
              <span className="text-xs font-medium text-white uppercase tracking-wide">Cost Breakdown</span>
              <span className="text-[9px] text-gray-500">(Click values to edit)</span>
            </div>

            <div className="space-y-3">
              {/* Manufacturing Costs */}
              <div className="p-2 bg-gray-900/50 rounded-lg">
                <div className="text-[10px] text-gray-400 uppercase tracking-wide mb-2">Manufacturing</div>
                <div className="space-y-1">
                  <EditableCostRow 
                    label="Raw Materials" 
                    field="raw_materials_cost" 
                    currencySymbol={currencySymbol} 
                    standardCosts={standardCosts} 
                    standardPercents={standardPercents}
                    handleStandardCostChange={handleStandardCostChange} 
                    handleStandardCostBlur={handleStandardCostBlur} 
                    setIsEditingStandardCost={setIsEditingStandardCost}
                    baseValue={0}  // No percentage for raw materials (it's the base)
                  />
                  <EditableCostRow 
                    label="Labor" 
                    field="labor_cost" 
                    currencySymbol={currencySymbol} 
                    standardCosts={standardCosts} 
                    standardPercents={standardPercents}
                    handleStandardCostChange={handleStandardCostChange} 
                    handleStandardCostBlur={handleStandardCostBlur} 
                    setIsEditingStandardCost={setIsEditingStandardCost}
                    baseValue={rawMaterialsCost}
                    baseLabel="of raw materials"
                    onPercentChange={handlePercentChange}
                    editingPercent={editingPercent}
                    setEditingPercent={setEditingPercent}
                  />
                  <EditableCostRow 
                    label="Factory Overhead" 
                    field="factory_overhead" 
                    currencySymbol={currencySymbol} 
                    standardCosts={standardCosts} 
                    standardPercents={standardPercents}
                    handleStandardCostChange={handleStandardCostChange} 
                    handleStandardCostBlur={handleStandardCostBlur} 
                    setIsEditingStandardCost={setIsEditingStandardCost}
                    baseValue={rawMaterialsCost}
                    baseLabel="of raw materials"
                    onPercentChange={handlePercentChange}
                    editingPercent={editingPercent}
                    setEditingPercent={setEditingPercent}
                  />
                  <div className="flex justify-between items-center text-xs pt-1 border-t border-gray-700">
                    <span className="text-gray-300 font-medium">Subtotal</span>
                    <span className="text-blue-400 font-mono">{currencySymbol}{safeToFixed(manufacturingCost)}</span>
                  </div>
                </div>
              </div>

              {/* Import/Shipping Costs */}
              <div className="p-2 bg-gray-900/50 rounded-lg">
                <div className="text-[10px] text-gray-400 uppercase tracking-wide mb-2">Import & Shipping</div>
                <div className="space-y-1">
                  <EditableCostRow 
                    label="Freight" 
                    field="freight_cost" 
                    currencySymbol={currencySymbol} 
                    standardCosts={standardCosts} 
                    standardPercents={standardPercents}
                    handleStandardCostChange={handleStandardCostChange} 
                    handleStandardCostBlur={handleStandardCostBlur} 
                    setIsEditingStandardCost={setIsEditingStandardCost}
                    baseValue={manufacturingCost}
                    baseLabel="of mfg"
                    onPercentChange={handlePercentChange}
                    editingPercent={editingPercent}
                    setEditingPercent={setEditingPercent}
                  />
                  <EditableCostRow 
                    label="Duties" 
                    field="duties_cost" 
                    currencySymbol={currencySymbol} 
                    standardCosts={standardCosts} 
                    standardPercents={standardPercents}
                    handleStandardCostChange={handleStandardCostChange} 
                    handleStandardCostBlur={handleStandardCostBlur} 
                    setIsEditingStandardCost={setIsEditingStandardCost}
                    baseValue={manufacturingCost}
                    baseLabel="of mfg"
                    onPercentChange={handlePercentChange}
                    editingPercent={editingPercent}
                    setEditingPercent={setEditingPercent}
                  />
                  <EditableCostRow 
                    label="Handling" 
                    field="handling_cost" 
                    currencySymbol={currencySymbol} 
                    standardCosts={standardCosts} 
                    standardPercents={standardPercents}
                    handleStandardCostChange={handleStandardCostChange} 
                    handleStandardCostBlur={handleStandardCostBlur} 
                    setIsEditingStandardCost={setIsEditingStandardCost}
                    baseValue={manufacturingCost}
                    baseLabel="of mfg"
                    onPercentChange={handlePercentChange}
                    editingPercent={editingPercent}
                    setEditingPercent={setEditingPercent}
                  />
                  <div className="flex justify-between items-center text-xs pt-1 border-t border-gray-700">
                    <span className="text-gray-300 font-medium">Subtotal</span>
                    <span className="text-blue-400 font-mono">{currencySymbol}{safeToFixed(importShippingCost)}</span>
                  </div>
                </div>
              </div>

              {/* Fulfillment */}
              <EditableCostRow 
                label="Fulfillment" 
                field="fulfillment_cost" 
                currencySymbol={currencySymbol} 
                standardCosts={standardCosts} 
                standardPercents={standardPercents}
                handleStandardCostChange={handleStandardCostChange} 
                handleStandardCostBlur={handleStandardCostBlur} 
                setIsEditingStandardCost={setIsEditingStandardCost}
                baseValue={0}  // No percentage for fulfillment
              />
            </div>
          </CardContent>
        </Card>

        {/* Total COGS */}
        <Card className="bg-gradient-to-r from-cyan-900/50 to-cyan-800/50 border-cyan-600">
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <DollarSign className="w-4 h-4 text-cyan-400" />
                <span className="text-xs font-medium text-white uppercase tracking-wide">Total COGS</span>
              </div>
              <span className="text-xl font-bold text-white font-mono">
                {currencySymbol}{safeToFixed(totalCogs)}
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Pricing */}
        <Card className="bg-gray-800 border-gray-700">
          <CardContent className="p-3">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="w-4 h-4 text-purple-400" />
              <span className="text-xs font-medium text-white uppercase tracking-wide">Pricing & Margins</span>
            </div>

            <div className="space-y-2">
              <div className="flex justify-between items-center text-xs">
                <span className="text-gray-400">Wholesale Price</span>
                <div className="flex items-center gap-2">
                  <span className="text-white font-mono">{currencySymbol}{safeToFixed(wholesalePrice)}</span>
                  <Badge variant={wholesaleMargin >= 25 ? "success" : "destructive"} className="text-[10px]">
                    {safeToFixed(wholesaleMargin, 1)}% margin
                  </Badge>
                </div>
              </div>
              <div className="flex justify-between items-center text-xs">
                <span className="text-gray-400">Retail Price</span>
                <div className="flex items-center gap-2">
                  <span className="text-white font-mono">{currencySymbol}{safeToFixed(retailPrice)}</span>
                  <Badge variant={retailMargin >= 40 ? "success" : "destructive"} className="text-[10px]">
                    {safeToFixed(retailMargin, 1)}% margin
                  </Badge>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Info Note */}
        <div className="flex items-start gap-2 p-2 bg-gray-800/50 rounded-lg border border-gray-700">
          <AlertCircle className="w-4 h-4 text-cyan-400 mt-0.5 flex-shrink-0" />
          <p className="text-[10px] text-gray-400">
            Click on any cost value to edit. Changes are saved automatically when you click away.
          </p>
        </div>
      </div>
    );
  }

  // ============================================================
  // FINISHED PRODUCT PRICING UI
  // ============================================================
  const componentCost = safeNumber(costingData?.component_cost ?? productData.component_cost);
  
  // Get split fulfillment costs from API (used as fallback)
  const apiPackagingCost = safeNumber(costingData?.packaging_cost ?? productData.packaging_cost);
  const apiShippingCost = safeNumber(costingData?.shipping_cost ?? productData.shipping_cost);
  const fulfillmentCost = safeNumber(costingData?.fulfillment_cost ?? productData.fulfillment_cost);
  
  // ALWAYS calculate costs locally based on current effective percentages
  // This ensures the display updates immediately when user changes override values
  const effectivePackagingPercent = getEffectivePercent('packaging_handling');
  const effectiveShippingPercent = getEffectivePercent('shipping');
  
  // Calculate packaging and shipping costs using effective percentages
  // These values update immediately when user changes custom percentages
  const effectivePackagingCost = componentCost * (effectivePackagingPercent / 100);
  const effectiveShippingCost = componentCost * (effectiveShippingPercent / 100);
  
  // For display in the UI - always use calculated values
  const displayPackagingCost = effectivePackagingCost;
  const displayShippingCost = effectiveShippingCost;
  
  // Per-channel COGS - calculated based on current shipping_paid_by selection
  const wholesaleShippingPaidBy = pricingConfig.wholesale?.shipping_paid_by || 'seller';
  const retailShippingPaidBy = pricingConfig.retail?.shipping_paid_by || 'seller';
  
  // Calculate channel-specific COGS
  const wholesaleCogs = wholesaleShippingPaidBy === 'customer'
    ? componentCost + effectivePackagingCost  // Customer pays shipping - exclude shipping
    : componentCost + effectivePackagingCost + effectiveShippingCost;  // Seller pays - include shipping
    
  const retailCogs = retailShippingPaidBy === 'customer'
    ? componentCost + effectivePackagingCost  // Customer pays shipping - exclude shipping
    : componentCost + effectivePackagingCost + effectiveShippingCost;  // Seller pays - include shipping
  
  const totalCogs = safeNumber(costingData?.total_cogs ?? productData.total_cogs ?? (componentCost + effectivePackagingCost + effectiveShippingCost));
  
  // Suggested prices - recalculate based on channel COGS
  const wholesaleMarginTarget = controlSheet.wholesale_margin_target / 100;
  const retailMarginTarget = controlSheet.retail_margin_target / 100;
  const suggestedWholesale = wholesaleMarginTarget < 1 ? wholesaleCogs / (1 - wholesaleMarginTarget) : 0;
  const suggestedRetail = retailMarginTarget < 1 ? retailCogs / (1 - retailMarginTarget) : 0;
  
  const wholesalePrice = safeNumber(productData.wholesale_price);
  const retailPrice = safeNumber(productData.retail_price);
  const bomItems = costingData?.bom || productData.bom || [];

  // Calculate actual margins (using channel-specific COGS)
  const wholesaleMargin = wholesalePrice > 0 ? ((wholesalePrice - wholesaleCogs) / wholesalePrice * 100) : 0;
  const retailMargin = retailPrice > 0 ? ((retailPrice - retailCogs) / retailPrice * 100) : 0;
  const wholesaleProfit = wholesalePrice - wholesaleCogs;
  const retailProfit = retailPrice - retailCogs;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Layers className="w-4 h-4 text-purple-400" />
          <span className="text-sm font-semibold text-white">Finished Product Pricing</span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleRecalculate}
            disabled={loading || !productId}
            className="h-7 text-xs"
            data-testid="recalculate-finished-costs-btn"
          >
            <RefreshCw className={`w-3 h-3 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Recalculate
          </Button>
        </div>
      </div>

      {/* Costing Mode Toggle */}
      <Card className="bg-gray-800 border-gray-700">
        <CardContent className="p-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Zap className="w-4 h-4 text-yellow-400" />
              <span className="text-xs text-gray-300">Costing Mode</span>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleToggleCostingMode}
                className={`px-3 py-1 text-xs rounded-full transition-colors ${
                  productData.costing_mode === 'auto'
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-700 text-gray-400'
                }`}
              >
                AUTO
              </button>
              <button
                onClick={handleToggleCostingMode}
                className={`px-3 py-1 text-xs rounded-full transition-colors ${
                  productData.costing_mode === 'manual'
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-700 text-gray-400'
                }`}
              >
                MANUAL
              </button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Cost Rollup */}
      <Card className="bg-gray-800 border-gray-700">
        <CardContent className="p-3">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Calculator className="w-4 h-4 text-green-400" />
              <span className="text-xs font-medium text-white uppercase tracking-wide">Cost Rollup</span>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onNavigateToBOM}
              className="h-6 text-[10px] text-blue-400 hover:text-blue-300"
              data-testid="view-bom-btn"
            >
              View BOM <ExternalLink className="w-3 h-3 ml-1" />
            </Button>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between items-center text-xs">
              <span className="text-gray-400">Component Cost (from BOM)</span>
              <span className="text-white font-mono">{currencySymbol}{safeToFixed(componentCost)}</span>
            </div>
            {bomItems.length === 0 && (
              <div className="text-[10px] text-yellow-400 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                No BOM configured - Add components in the BOM tab
              </div>
            )}
            
            {/* Fulfillment Section - Split into Packaging + Shipping */}
            <div className="p-2 rounded-lg bg-gray-900/50 space-y-2">
              <div className="flex items-center gap-2 text-xs text-gray-300">
                <Package className="w-3 h-3 text-green-400" />
                <span>Fulfillment Costs</span>
              </div>
              
              {/* Packaging & Handling */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-gray-400">+ Packaging &amp; Handling</span>
                  <button
                    onClick={() => handleOverrideToggle('packaging_handling')}
                    className={`flex items-center gap-1 px-1.5 py-0.5 text-[9px] rounded transition-colors ${
                      isUsingOverride('packaging_handling')
                        ? 'bg-purple-600/30 text-purple-400 border border-purple-500'
                        : 'bg-gray-700 text-gray-500 border border-gray-600'
                    }`}
                    data-testid="packaging-override-toggle"
                  >
                    {isUsingOverride('packaging_handling') ? 'Custom' : 'Global'}
                  </button>
                  {isUsingOverride('packaging_handling') ? (
                    <div className="flex items-center gap-1">
                      <input
                        type="text"
                        inputMode="decimal"
                        value={getInputDisplayValue('packaging_handling')}
                        onFocus={() => handleInputFocus('packaging_handling')}
                        onChange={(e) => handleInputChange('packaging_handling', e.target.value)}
                        onKeyDown={(e) => handleInputKeyDown(e, 'packaging_handling')}
                        className="w-16 h-6 px-2 text-xs text-center bg-gray-700 border border-purple-500 text-white rounded"
                        style={{ minWidth: '60px' }}
                        data-testid="packaging-override-input"
                        placeholder="0"
                      />
                      <span className="text-[10px] text-gray-500">%</span>
                    </div>
                  ) : (
                    <span className="text-[9px] text-gray-500">{controlSheet.packaging_handling_percent || 0}%</span>
                  )}
                </div>
                <span className="text-green-400 font-mono text-xs">+{currencySymbol}{safeToFixed(displayPackagingCost)}</span>
              </div>
              
              {/* Shipping */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-gray-400">+ Shipping</span>
                  <button
                    onClick={() => handleOverrideToggle('shipping')}
                    className={`flex items-center gap-1 px-1.5 py-0.5 text-[9px] rounded transition-colors ${
                      isUsingOverride('shipping')
                        ? 'bg-purple-600/30 text-purple-400 border border-purple-500'
                        : 'bg-gray-700 text-gray-500 border border-gray-600'
                    }`}
                    data-testid="shipping-override-toggle"
                  >
                    {isUsingOverride('shipping') ? 'Custom' : 'Global'}
                  </button>
                  {isUsingOverride('shipping') ? (
                    <div className="flex items-center gap-1">
                      <input
                        type="text"
                        inputMode="decimal"
                        value={getInputDisplayValue('shipping')}
                        onFocus={() => handleInputFocus('shipping')}
                        onChange={(e) => handleInputChange('shipping', e.target.value)}
                        onKeyDown={(e) => handleInputKeyDown(e, 'shipping')}
                        className="w-16 h-6 px-2 text-xs text-center bg-gray-700 border border-purple-500 text-white rounded"
                        style={{ minWidth: '60px' }}
                        data-testid="shipping-override-input"
                        placeholder="0"
                      />
                      <span className="text-[10px] text-gray-500">%</span>
                    </div>
                  ) : (
                    <span className="text-[9px] text-gray-500">{controlSheet.shipping_percent || 0}%</span>
                  )}
                </div>
                <span className="text-orange-400 font-mono text-xs">+{currencySymbol}{safeToFixed(displayShippingCost)}</span>
              </div>
              
              <div className="flex items-start gap-1 pt-1 border-t border-gray-700/50">
                <AlertCircle className="w-3 h-3 text-blue-400 mt-0.5 flex-shrink-0" />
                <p className="text-[9px] text-gray-500">
                  Shipping cost only applies when seller pays shipping
                </p>
              </div>
            </div>
            
            {/* Show both COGS scenarios */}
            <div className="pt-2 border-t border-gray-700 space-y-1">
              <div className="flex justify-between items-center text-xs">
                <span className="text-gray-400">COGS (customer pays shipping)</span>
                <span className="text-green-400 font-mono">{currencySymbol}{safeToFixed(componentCost + effectivePackagingCost)}</span>
              </div>
              <div className="flex justify-between items-center text-xs">
                <span className="text-gray-400">COGS (seller pays shipping)</span>
                <span className="text-orange-400 font-mono">{currencySymbol}{safeToFixed(componentCost + effectivePackagingCost + effectiveShippingCost)}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Pricing Table */}
      <Card className="bg-gray-800 border-gray-700">
        <CardContent className="p-3">
          <div className="flex items-center gap-2 mb-3">
            <DollarSign className="w-4 h-4 text-purple-400" />
            <span className="text-xs font-medium text-white uppercase tracking-wide">Pricing</span>
          </div>

          {/* Pricing Grid */}
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-gray-400">
                  <th className="text-left py-1 font-medium">Channel</th>
                  <th className="text-center py-1 font-medium">Shipping</th>
                  <th className="text-right py-1 font-medium">COGS</th>
                  <th className="text-right py-1 font-medium">Target</th>
                  <th className="text-right py-1 font-medium">Suggested</th>
                  <th className="text-right py-1 font-medium">Your Price</th>
                  <th className="text-center py-1 font-medium">Mode</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {/* Wholesale Row */}
                <tr>
                  <td className="py-2 text-gray-300">Wholesale</td>
                  <td className="py-2 text-center">
                    <select
                      value={pricingConfig.wholesale?.shipping_paid_by || 'seller'}
                      onChange={(e) => handleShippingPaidByChange('wholesale', e.target.value)}
                      className="bg-gray-700 border-gray-600 text-[10px] text-gray-300 rounded px-1 py-0.5"
                      data-testid="wholesale-shipping-select"
                    >
                      <option value="customer">Customer</option>
                      <option value="seller">Seller</option>
                    </select>
                  </td>
                  <td className="py-2 text-right text-gray-400 font-mono text-[10px]">
                    {currencySymbol}{safeToFixed(wholesaleCogs)}
                  </td>
                  <td className="py-2 text-right text-gray-400">{controlSheet.wholesale_margin_target}%</td>
                  <td className="py-2 text-right text-gray-300 font-mono">{currencySymbol}{safeToFixed(suggestedWholesale)}</td>
                  <td className="py-2 text-right">
                    <div className="relative inline-block">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">{currencySymbol}</span>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={wholesalePrice}
                        onChange={(e) => onFieldChange('wholesale_price', parseFloat(e.target.value) || 0)}
                        disabled={productData.wholesale_price_mode === 'auto'}
                        className={`w-20 h-7 text-xs text-right pl-5 pr-2 ${
                          productData.wholesale_price_mode === 'auto'
                            ? 'bg-gray-700/50 text-gray-400'
                            : 'bg-gray-700 text-white'
                        } border-gray-600`}
                        data-testid="wholesale-price-input"
                      />
                    </div>
                  </td>
                  <td className="py-2 text-center">
                    <button
                      onClick={() => handleTogglePriceMode('wholesale')}
                      className={`px-2 py-0.5 text-[10px] rounded ${
                        productData.wholesale_price_mode === 'auto'
                          ? 'bg-blue-600/30 text-blue-400'
                          : 'bg-purple-600/30 text-purple-400'
                      }`}
                      data-testid="wholesale-mode-toggle"
                    >
                      {productData.wholesale_price_mode === 'auto' ? (
                        <><Unlock className="w-3 h-3 inline mr-0.5" />AUTO</>
                      ) : (
                        <><Lock className="w-3 h-3 inline mr-0.5" />MANUAL</>
                      )}
                    </button>
                  </td>
                </tr>

                {/* Retail Row */}
                <tr>
                  <td className="py-2 text-gray-300">Retail</td>
                  <td className="py-2 text-center">
                    <select
                      value={pricingConfig.retail?.shipping_paid_by || 'seller'}
                      onChange={(e) => handleShippingPaidByChange('retail', e.target.value)}
                      className="bg-gray-700 border-gray-600 text-[10px] text-gray-300 rounded px-1 py-0.5"
                      data-testid="retail-shipping-select"
                    >
                      <option value="customer">Customer</option>
                      <option value="seller">Seller</option>
                    </select>
                  </td>
                  <td className="py-2 text-right text-gray-400 font-mono text-[10px]">
                    {currencySymbol}{safeToFixed(retailCogs)}
                  </td>
                  <td className="py-2 text-right text-gray-400">{controlSheet.retail_margin_target}%</td>
                  <td className="py-2 text-right text-gray-300 font-mono">{currencySymbol}{safeToFixed(suggestedRetail)}</td>
                  <td className="py-2 text-right">
                    <div className="relative inline-block">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-xs">{currencySymbol}</span>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={retailPrice}
                        onChange={(e) => onFieldChange('retail_price', parseFloat(e.target.value) || 0)}
                        disabled={productData.retail_price_mode === 'auto'}
                        className={`w-20 h-7 text-xs text-right pl-5 pr-2 ${
                          productData.retail_price_mode === 'auto'
                            ? 'bg-gray-700/50 text-gray-400'
                            : 'bg-gray-700 text-white'
                        } border-gray-600`}
                        data-testid="retail-price-input"
                      />
                    </div>
                  </td>
                  <td className="py-2 text-center">
                    <button
                      onClick={() => handleTogglePriceMode('retail')}
                      className={`px-2 py-0.5 text-[10px] rounded ${
                        productData.retail_price_mode === 'auto'
                          ? 'bg-blue-600/30 text-blue-400'
                          : 'bg-purple-600/30 text-purple-400'
                      }`}
                      data-testid="retail-mode-toggle"
                    >
                      {productData.retail_price_mode === 'auto' ? (
                        <><Unlock className="w-3 h-3 inline mr-0.5" />AUTO</>
                      ) : (
                        <><Lock className="w-3 h-3 inline mr-0.5" />MANUAL</>
                      )}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Actual Margins */}
      <Card className="bg-gray-800 border-gray-700">
        <CardContent className="p-3">
          <div className="flex items-center gap-2 mb-3">
            <TrendingUp className="w-4 h-4 text-green-400" />
            <span className="text-xs font-medium text-white uppercase tracking-wide">Actual Margins</span>
          </div>

          <div className="grid grid-cols-2 gap-3">
            {/* Wholesale Margin */}
            <div className={`p-2 rounded border ${wholesaleMargin >= controlSheet.wholesale_margin_target ? 'bg-green-900/30 border-green-700' : 'bg-yellow-900/30 border-yellow-700'}`}>
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-gray-400 uppercase">Wholesale</span>
                {wholesaleMargin >= controlSheet.wholesale_margin_target ? (
                  <Badge variant="outline" className="text-[8px] text-green-400 border-green-600">✓ On Target</Badge>
                ) : (
                  <Badge variant="outline" className="text-[8px] text-yellow-400 border-yellow-600">Below Target</Badge>
                )}
              </div>
              <p className={`text-lg font-bold ${wholesaleMargin >= controlSheet.wholesale_margin_target ? 'text-green-400' : 'text-yellow-400'}`}>
                {safeToFixed(wholesaleMargin, 1)}%
              </p>
              <p className="text-[10px] text-gray-400">
                {currencySymbol}{safeToFixed(wholesaleProfit)} profit
              </p>
            </div>

            {/* Retail Margin */}
            <div className={`p-2 rounded border ${retailMargin >= controlSheet.retail_margin_target ? 'bg-green-900/30 border-green-700' : 'bg-yellow-900/30 border-yellow-700'}`}>
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-gray-400 uppercase">Retail</span>
                {retailMargin >= controlSheet.retail_margin_target ? (
                  <Badge variant="outline" className="text-[8px] text-green-400 border-green-600">✓ On Target</Badge>
                ) : (
                  <Badge variant="outline" className="text-[8px] text-yellow-400 border-yellow-600">Below Target</Badge>
                )}
              </div>
              <p className={`text-lg font-bold ${retailMargin >= controlSheet.retail_margin_target ? 'text-green-400' : 'text-yellow-400'}`}>
                {safeToFixed(retailMargin, 1)}%
              </p>
              <p className="text-[10px] text-gray-400">
                {currencySymbol}{safeToFixed(retailProfit)} profit
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Info Note */}
      <div className="flex items-start gap-2 p-2 bg-gray-800/50 rounded-lg border border-gray-700">
        <AlertCircle className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
        <p className="text-[10px] text-gray-400">
          Costs and suggested prices are calculated from your BOM and Control Sheet settings.
          When in AUTO mode, prices auto-update when raw material costs or percentages change.
          Switch to MANUAL to override with custom prices.
        </p>
      </div>
    </div>
  );
});

// Set display name for debugging
PricingTab.displayName = 'PricingTab';

export default PricingTab;
